import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

// Public, unauthenticated site configuration. Reads GET /api/config.
// If the endpoint isn't available we safely default to "live, no charity".
const DEFAULTS = {
  maintenanceMode: false,
  applePay: true,
  googlePay: true,
  charityName: "Feed the Homeless Fund",
  charityRoundupEnabled: false,
  charityRoundupMax: 100,
  charityTotalRaised: 0,
  stripeReady: false,
  stripePublishableKey: "",
  launchAt: null,
  showTicketCounts: false,
  socialLinks: {},
  gaMeasurementId: "",
  trustpilotUrl: "",
  iosAppUrl: "",
  androidAppUrl: "",
  contactEmail: "",
  contactPhone: "",
  contactAddress: "",
};

const SiteConfigContext = createContext({ ...DEFAULTS, refresh: () => {} });
export const useSiteConfig = () => useContext(SiteConfigContext);

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/config");
      setConfig({
        maintenanceMode: !!data.maintenance_mode,
        applePay: data.apple_pay !== false,
        googlePay: data.google_pay !== false,
        charityName: data.charity_name || DEFAULTS.charityName,
        charityRoundupEnabled: !!data.charity_roundup_enabled,
        charityRoundupMax: Number(data.charity_roundup_max) || DEFAULTS.charityRoundupMax,
        charityTotalRaised: Number(data.charity_total_raised) || 0,
        stripeReady: !!data.stripe_ready,
        stripePublishableKey: data.stripe_publishable_key || "",
        launchAt: data.launch_at || null,
        showTicketCounts: !!data.show_ticket_counts,
        socialLinks: data.social_links || {},
        gaMeasurementId: data.ga_measurement_id || "",
        trustpilotUrl: data.trustpilot_url || "",
        iosAppUrl: data.ios_app_url || "",
        androidAppUrl: data.android_app_url || "",
        contactEmail: data.contact_email || "",
        contactPhone: data.contact_phone || "",
        contactAddress: data.contact_address || "",
      });
    } catch {
      /* /config not available yet -> keep defaults */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  return <SiteConfigContext.Provider value={{ ...config, refresh }}>{children}</SiteConfigContext.Provider>;
};
